import * as React from "react";
import { Autocomplete, TextField } from "@mui/material";
import { GeoPlacesClient, SuggestCommand, SearchTextCommand } from "@aws-sdk/client-geo-places";
import { withAPIKey } from "@aws/amazon-location-utilities-auth-helper";

const DEBOUNCE_MS = 300;
const MIN_LENGTH = 2;
/** Default [lng, lat] to bias suggestions toward continental US */
const DEFAULT_US_BIAS = [-98.5, 39.5];

/**
 * Address autocomplete using Amazon Location Service Places API (geo-places client).
 *
 * Uses @aws-sdk/client-geo-places with Suggest and SearchText. Authenticate with an API key
 * (Places resource) via REACT_APP_AWS_LOCATION_API_KEY and REACT_APP_AWS_LOCATION_REGION.
 *
 * Env (in .env):
 *   REACT_APP_AWS_LOCATION_API_KEY  - Your Places API key
 *   REACT_APP_AWS_LOCATION_REGION  - e.g. us-east-1
 *
 * @param {string} value - Current address text
 * @param {(address: string) => void} onAddressChange - Called when user selects an option or types
 * @param {object} [sx] - MUI sx prop
 * @param {string} [label='Address']
 * @param {string} [placeholder]
 * @param {string} [size] - 'small' | 'medium'
 * @param {boolean} [required]
 * @param {boolean} [disabled]
 * @param {[number, number]} [biasPosition] - Optional [longitude, latitude] to prefer results near this point.
 */
export default function AddressAutocomplete({
  value,
  onAddressChange,
  sx,
  label = "Address",
  placeholder = "Start typing an address…",
  size = "medium",
  required = false,
  disabled = false,
  biasPosition,
}) {
  const apiKey = process.env.REACT_APP_AWS_LOCATION_API_KEY || "";
  const region = process.env.REACT_APP_AWS_LOCATION_REGION || "";

  const client = React.useMemo(() => {
    if (!apiKey || !region) return null;
    return new GeoPlacesClient(withAPIKey(apiKey, region).getClientConfig());
  }, [apiKey, region]);

  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(value ?? "");
  const [navLabel, setNavLabel] = React.useState(null);
  const highlightedRef = React.useRef(null);
  const debounceRef = React.useRef(null);

  const displayValue = navLabel ?? inputValue;

  React.useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const bias = React.useMemo(
    () => (Array.isArray(biasPosition) && biasPosition.length >= 2 ? biasPosition : DEFAULT_US_BIAS),
    [biasPosition]
  );

  const fetchSuggestions = React.useCallback(
    async (query) => {
      const trimmed = typeof query === "string" ? query.trim() : "";
      if (!client || !trimmed || trimmed.length < MIN_LENGTH) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const command = new SuggestCommand({
          QueryText: trimmed,
          MaxResults: 15,
          Filter: { IncludeCountries: ["USA"] },
          AdditionalFeatures: ["Core"],
          BiasPosition: bias,
        });
        const response = await client.send(command);
        const items = Array.isArray(response.ResultItems) ? response.ResultItems : [];
        setOptions(items);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [client, bias]
  );

  const fetchPlaceByQueryId = React.useCallback(
    async (queryId) => {
      if (!client || !queryId) return null;
      try {
        const command = new SearchTextCommand({
          QueryId: queryId,
          MaxResults: 1,
          AdditionalFeatures: ["Core"],
        });
        const response = await client.send(command);
        const first =
          Array.isArray(response.ResultItems) && response.ResultItems.length > 0
            ? response.ResultItems[0]
            : null;
        return first;
      } catch {
        return null;
      }
    },
    [client]
  );

  const handleInputChange = React.useCallback(
    (_, newInputValue, reason) => {
      if (reason !== "input") return;
      setNavLabel(null);
      highlightedRef.current = null;
      setInputValue(newInputValue);
      onAddressChange(newInputValue);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(newInputValue);
        debounceRef.current = null;
      }, DEBOUNCE_MS);
    },
    [fetchSuggestions, onAddressChange]
  );

  const handleChange = React.useCallback(
    async (_, newValue) => {
      setNavLabel(null);
      highlightedRef.current = null;
      if (typeof newValue === "string") {
        onAddressChange(newValue);
        setInputValue(newValue);
        return;
      }
      if (!newValue) {
        onAddressChange("");
        setInputValue("");
        return;
      }
      let addressLabel = newValue.Place?.Address?.Label ?? null;
      if (newValue.Query?.QueryId) {
        const fullPlace = await fetchPlaceByQueryId(newValue.Query.QueryId);
        if (fullPlace) {
          addressLabel = fullPlace.Address?.Label ?? addressLabel;
        }
      }
      const display = addressLabel || newValue.Title || "";
      onAddressChange(display);
      setInputValue(display);
    },
    [onAddressChange, fetchPlaceByQueryId]
  );

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const getOptionLabel = (option) => {
    if (typeof option === "string") return option;
    const addr = option.Place?.Address?.Label;
    if (addr) return addr;
    return option.Title ?? "";
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionLabel={getOptionLabel}
      value={value ?? ""}
      inputValue={displayValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      loading={loading}
      filterOptions={(x) => x}
      // Suggestions can share an identical label (e.g. a venue and a query for the same address),
      // and MUI keys options by label by default, so supply a unique key.
      renderOption={(props, option) => {
        const { key: _ignored, ...liProps } = props;
        const id = option.Place?.PlaceId ?? option.Query?.QueryId ?? "";
        return (
          <li key={`${id}-${options.indexOf(option)}`} {...liProps}>
            {getOptionLabel(option)}
          </li>
        );
      }}
      onHighlightChange={(_, option) => {
        highlightedRef.current = option;
        setNavLabel(option ? getOptionLabel(option) : null);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          required={required}
          disabled={disabled}
          inputProps={{
            ...params.inputProps,
            placeholder,
            onKeyDown: (e) => {
              if (e.key === "Tab" && highlightedRef.current) {
                const selected = getOptionLabel(highlightedRef.current);
                onAddressChange(selected);
                setInputValue(selected);
                setNavLabel(null);
                highlightedRef.current = null;
              }
              params.inputProps?.onKeyDown?.(e);
            },
          }}
        />
      )}
      sx={{ minWidth: 200, ...sx }}
      ListboxProps={{
        sx: {
          maxHeight: 280,
          "& .MuiAutocomplete-option": {
            color: "text.primary",
          },
          "& .MuiAutocomplete-option.Mui-focused": {
            bgcolor: "action.hover",
            color: "text.primary",
          },
        },
      }}
      isOptionEqualToValue={(option, val) => {
        if (typeof val === "string") return getOptionLabel(option) === val;
        return option === val;
      }}
    />
  );
}
